// Truy vấn khách hàng (crm). Phạm vi own/team/all do crm-service lọc — FE không lọc theo quyền. Hook nhận `enabled`;
// điều kiện quyền / module do lớp trên tính (useCan, useViewer) rồi truyền vào.
import { useEffect } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { normPhone } from '@cathouse/contracts';
import { ApiError, http, qk } from '@/shared/api';
import { syncServerTime, useDebounced } from '@/shared/lib';
import {
  LIST_LIMIT,
  type AssignableStaff,
  type CustomerActivityPage,
  type CustomerDeal,
  type CustomerDetail,
  type CustomerExportView,
  type CustomerFacetFilters,
  type CustomerFacets,
  type CustomerImportView,
  type CustomerList,
  type CustomerListFilters,
  type CustomerOption,
  type CustomerSearchHit,
  type CustomerTask,
  type PhoneDuplicate,
  type UnitMatch,
} from './model';

const sortParam = (f: Pick<CustomerListFilters, 'sort' | 'dir'>) => (f.dir === 'desc' ? `-${f.sort}` : f.sort);

/** Danh sách khách (300 dòng đầu, sắp ở server). */
export const useCustomers = (f: CustomerListFilters, enabled = true) =>
  useQuery({
    queryKey: qk.customers.list(f),
    queryFn: async ({ signal }) => {
      const { sort: _s, dir: _d, ...rest } = f;
      const r = await http.get<CustomerList>('/crm/customers', { query: { ...rest, sort: sortParam(f), page: 1, pageSize: LIST_LIMIT }, signal });
      syncServerTime(r.serverTime);
      return r;
    },
    placeholderData: keepPreviousData,
    enabled,
  });

/** Số trên chip, số theo giai đoạn, danh sách người phụ trách (trên toàn tập khách xem được). */
export const useCustomerFacets = (f: CustomerFacetFilters, enabled = true) =>
  useQuery({
    queryKey: qk.customers.facets(f),
    queryFn: ({ signal }) => http.get<CustomerFacets>('/crm/customers/facets', { query: { ...f }, signal }),
    placeholderData: keepPreviousData,
    enabled,
  });

/**
 * 404 / 403 của hồ sơ: khách đã bị xoá hoặc không còn trong phạm vi xem (chuyển cho người khác, thu hồi về kho chung…);
 * 400 / 422 VALIDATION: mã khách hỏng trong liên kết ⇒ cũng là "không tìm thấy" (không phải lỗi kết nối).
 */
export const customerGone = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 403 || ((e.status === 400 || e.status === 422) && e.code === 'VALIDATION'));

/**
 * Hồ sơ khách (kèm permissions, profileVersion). 404 / 403 ⇒ không tìm thấy / ngoài phạm vi (`customerGone(error)`).
 * Mất quyền xem khi đang mở (làm mới theo realtime trả 404): TanStack vẫn giữ bản cũ trong `data` ⇒ đặt lại truy vấn để
 * bỏ bản cũ (số điện thoại, ghi chú…) khỏi bộ nhớ đệm — mọi nơi đang đọc hồ sơ này cùng thấy "không tìm thấy".
 */
export function useCustomer(id: string | null | undefined) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.customers.detail(id ?? ''),
    queryFn: ({ signal }) => http.get<CustomerDetail>(`/crm/customers/${id}`, { signal }),
    enabled: !!id,
  });
  const stale = !!id && q.isError && customerGone(q.error) && q.data !== undefined;
  useEffect(() => {
    if (stale && id) void qc.resetQueries({ queryKey: qk.customers.detail(id), exact: true });
  }, [stale, id, qc]);
  return q;
}

/** Lịch sử chăm sóc: 40 mục mới nhất, "Xem thêm" đọc tiếp (kể cả phần lưu trữ). */
export const useCustomerActivities = (id: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: qk.customers.activities(id),
    queryFn: ({ pageParam, signal }) =>
      http.get<CustomerActivityPage>(`/crm/customers/${id}/activities`, { query: { limit: 40, cursor: pageParam, archived: pageParam ? true : undefined }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });

/** Căn phù hợp đang trống (chấm theo ngân sách, loại căn, dự án, diện tích, hướng). */
export const useCustomerMatches = (id: string, o: { limit: number }, enabled = true) =>
  useQuery({
    queryKey: qk.customers.matches(id, o),
    queryFn: ({ signal }) => http.get<UnitMatch[]>(`/crm/customers/${id}/unit-matches`, { query: { limit: o.limit }, signal }),
    enabled,
  });

/** Lịch hẹn & công việc gắn với khách. */
export const useCustomerTasks = (id: string, enabled = true) =>
  useQuery({
    queryKey: qk.customers.tasks(id),
    queryFn: ({ signal }) => http.get<CustomerTask[]>(`/crm/customers/${id}/tasks`, { signal }),
    enabled,
  });

/** Giao dịch của khách (gateway hỏi crm rồi deal-service). Khoá dưới 'deals' để thao tác giao dịch làm mới hồ sơ. */
export const useCustomerDeals = (id: string, enabled = true) =>
  useQuery({
    queryKey: qk.deals.byCustomer(id),
    queryFn: ({ signal }) => http.get<CustomerDeal[]>(`/crm/customers/${id}/deals`, { signal }),
    retry: 1,
    enabled,
  });

/** Bộ chọn khách ở Chốt căn / phiếu cọc / lịch hẹn: nhãn "Tên · số che". */
export const useCustomerOptions = (purpose: 'lock' | 'booking' | 'task', q: string, enabled = true) =>
  useQuery({
    queryKey: qk.customers.options(purpose, q),
    queryFn: ({ signal }) => http.get<CustomerOption[]>('/crm/customers/options', { query: { purpose, q: q || undefined }, signal }),
    placeholderData: keepPreviousData,
    enabled,
  });

/**
 * Kiểm trùng SĐT khi gõ (hoãn 300 ms; chỉ tra khi số chuẩn hoá ≥ 10 chữ số). Không lọc phạm vi: báo trùng với mọi
 * khách để chặn. Kết quả chỉ dùng khi `data.normalized` khớp số đang nhập.
 */
export function usePhoneCheck(phone: string, exceptId: string | undefined, enabled = true) {
  const p = useDebounced(normPhone(phone), 300);
  return useQuery({
    queryKey: qk.customers.phoneCheck(p, exceptId),
    queryFn: ({ signal }) => http.get<PhoneDuplicate>('/crm/customers/phone-check', { query: { phone: p, exceptId }, signal }),
    enabled: enabled && p.length >= 10,
    staleTime: 15_000,
  });
}

/** Người nhận khách được phân (C8 ∩ phạm vi customers.assign). Dùng cả với queryClient.fetchQuery (hộp chọn người nhận). */
export const assigneesQuery = (purpose: 'manual' | 'auto' | 'import', o: { excludeStaffId?: string; teamId?: string } = {}) => ({
  queryKey: qk.customers.assignees(purpose, o),
  queryFn: () => http.get<AssignableStaff[]>('/crm/assignees', { query: { purpose, ...o } }),
  staleTime: 60_000,
});
export const useAssignees = (purpose: 'manual' | 'auto' | 'import', o: { excludeStaffId?: string; teamId?: string } = {}, enabled = true) =>
  useQuery({ ...assigneesQuery(purpose, o), enabled });

/** Loại sản phẩm có trong Nguồn hàng (ô "Loại sản phẩm"). */
export const useUnitTypes = (enabled = true) =>
  useQuery({ queryKey: qk.customers.unitTypes(), queryFn: () => http.get<string[]>('/crm/unit-types'), staleTime: 5 * 60_000, enabled });

/** Nguồn Tìm nhanh (Ctrl K). */
export const searchCustomers = (q: string, signal?: AbortSignal) => http.get<CustomerSearchHit[]>('/crm/customers/search', { query: { q, limit: 6 }, signal });

/** Lô nhập từ Excel/CSV (xem trước). Kết quả tải lên được đặt sẵn vào cache bằng setQueryData. */
export const useCustomerImport = (batchId: string | null) =>
  useQuery({
    queryKey: qk.customers.import(batchId ?? ''),
    queryFn: ({ signal }) => http.get<CustomerImportView>(`/crm/customer-imports/${batchId}`, { signal }),
    enabled: !!batchId,
    staleTime: Infinity,
  });

const EXPORT_DONE = new Set<CustomerExportView['status']>(['completed', 'failed', 'expired']);
/** Yêu cầu xuất CSV: hỏi lại mỗi giây tới khi xong / lỗi. */
export const useCustomerExport = (exportId: string | null) =>
  useQuery({
    queryKey: qk.customers.export(exportId ?? ''),
    queryFn: ({ signal }) => http.get<CustomerExportView>(`/crm/customer-exports/${exportId}`, { signal }),
    enabled: !!exportId,
    refetchInterval: (q) => (q.state.data && EXPORT_DONE.has(q.state.data.status) ? false : 1000),
    refetchOnWindowFocus: false,
  });
export const exportFinished = (v: CustomerExportView | undefined) => !!v && EXPORT_DONE.has(v.status);
